import {
  organizationEntitlementResponseSchema,
  superAdminAuditLogListResponseSchema,
  superAdminBetaFeatureItemSchema,
  superAdminBetaFeatureListResponseSchema,
  superAdminDashboardSummarySchema,
  superAdminOrganizationDirectoryResponseSchema,
  superAdminOrganizationDetailSchema,
  superAdminOrganizationListResponseSchema,
  superAdminOrganizationMessageBalanceResponseSchema,
  superAdminPlatformAdminListResponseSchema,
  superAdminPlatformAdminSchema,
  superAdminRevokeSessionsResponseSchema,
  superAdminSelfResponseSchema,
  superAdminUserDetailSchema,
  superAdminUserListResponseSchema,
  superAdminUserSessionListResponseSchema,
  systemConfigResponseSchema,
  systemSettingsResponseSchema,
  type BetaFeatureKey,
  type OrganizationEntitlementResponse,
  type OrganizationStatusName,
  type SuperAdminAuditActorKind,
  type SuperAdminAuditCategory,
  type SuperAdminAuditLogListResponse,
  type SuperAdminBetaFeatureItem,
  type SuperAdminBetaFeatureListResponse,
  type SuperAdminDashboardSummary,
  type SuperAdminOrganizationDirectoryResponse,
  type SuperAdminOrganizationDetail,
  type SuperAdminOrganizationListResponse,
  type SuperAdminOrganizationMessageBalanceResponse,
  type SuperAdminPlatformAdmin,
  type SuperAdminPlatformAdminListResponse,
  type SuperAdminRevokeSessionsResponse,
  type SuperAdminSelfResponse,
  type SuperAdminUpdateMessageBalanceRequest,
  type SuperAdminUserDetail,
  type SuperAdminUserListResponse,
  type SuperAdminUserSessionListResponse,
  type SystemConfigResponse,
  type SystemSettingsResponse,
  type UpdateOrganizationEntitlementRequest,
  type UpdateSystemConfigRequest,
  type UpdateSystemSettingsRequest,
} from "@multi-chat/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "./client.js";

export async function getSuperAdminMe(signal?: AbortSignal): Promise<SuperAdminSelfResponse> {
  return superAdminSelfResponseSchema.parse(await apiGet("/super-admin/me", signal));
}

export async function getSuperAdminDashboard(
  signal?: AbortSignal,
): Promise<SuperAdminDashboardSummary> {
  return superAdminDashboardSummarySchema.parse(
    await apiGet("/super-admin/organizations/summary", signal),
  );
}

export async function listSuperAdminOrganizations(
  query: { cursor?: string; search?: string; limit?: number },
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationListResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.search) params.set("search", query.search);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return superAdminOrganizationListResponseSchema.parse(
    await apiGet(`/super-admin/organizations${suffix}`, signal),
  );
}

/** Organizations a Platform Admin may enter from the workspace switcher. */
export async function listSuperAdminOrganizationDirectory(
  query: { cursor?: string; search?: string; limit?: number },
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationDirectoryResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.search) params.set("search", query.search);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return superAdminOrganizationDirectoryResponseSchema.parse(
    await apiGet(`/super-admin/organization-directory${suffix}`, signal),
  );
}

export async function getSuperAdminOrganization(
  organizationId: number,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationDetail> {
  return superAdminOrganizationDetailSchema.parse(
    await apiGet(`/super-admin/organizations/${organizationId}`, signal),
  );
}

export async function updateSuperAdminOrganizationStatus(
  organizationId: number,
  status: OrganizationStatusName,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationDetail> {
  return superAdminOrganizationDetailSchema.parse(
    await apiPatch(
      `/super-admin/organizations/${organizationId}/status`,
      { status },
      signal,
    ),
  );
}

export async function updateSuperAdminOrganizationBeta(
  organizationId: number,
  isBetaOrganization: boolean,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationDetail> {
  return superAdminOrganizationDetailSchema.parse(
    await apiPatch(
      `/super-admin/organizations/${organizationId}/beta`,
      { isBetaOrganization },
      signal,
    ),
  );
}

export async function listSuperAdminBetaFeatures(
  signal?: AbortSignal,
): Promise<SuperAdminBetaFeatureListResponse> {
  return superAdminBetaFeatureListResponseSchema.parse(
    await apiGet("/super-admin/beta-features", signal),
  );
}

export async function updateSuperAdminBetaFeature(
  featureKey: BetaFeatureKey,
  betaEnabled: boolean,
  signal?: AbortSignal,
): Promise<SuperAdminBetaFeatureItem> {
  return superAdminBetaFeatureItemSchema.parse(
    await apiPatch(`/super-admin/beta-features/${featureKey}`, { betaEnabled }, signal),
  );
}

export async function getSuperAdminConfig(signal?: AbortSignal): Promise<SystemConfigResponse> {
  return systemConfigResponseSchema.parse(await apiGet("/super-admin/config", signal));
}

export async function updateSuperAdminConfig(
  patch: UpdateSystemConfigRequest,
  signal?: AbortSignal,
): Promise<SystemConfigResponse> {
  return systemConfigResponseSchema.parse(await apiPatch("/super-admin/config", patch, signal));
}

export async function getSuperAdminSettings(signal?: AbortSignal): Promise<SystemSettingsResponse> {
  return systemSettingsResponseSchema.parse(await apiGet("/super-admin/settings", signal));
}

export async function updateSuperAdminSettings(
  patch: UpdateSystemSettingsRequest,
  signal?: AbortSignal,
): Promise<SystemSettingsResponse> {
  return systemSettingsResponseSchema.parse(await apiPatch("/super-admin/settings", patch, signal));
}

export async function listSuperAdminUsers(
  query: { cursor?: string; search?: string; limit?: number },
  signal?: AbortSignal,
): Promise<SuperAdminUserListResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.search) params.set("search", query.search);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return superAdminUserListResponseSchema.parse(await apiGet(`/super-admin/users${suffix}`, signal));
}

export async function getSuperAdminUser(userId: number, signal?: AbortSignal): Promise<SuperAdminUserDetail> {
  return superAdminUserDetailSchema.parse(await apiGet(`/super-admin/users/${userId}`, signal));
}

export async function listSuperAdminUserSessions(
  userId: number,
  signal?: AbortSignal,
): Promise<SuperAdminUserSessionListResponse> {
  return superAdminUserSessionListResponseSchema.parse(
    await apiGet(`/super-admin/users/${userId}/sessions`, signal),
  );
}

export async function revokeSuperAdminUserSession(
  userId: number,
  sessionId: string,
): Promise<SuperAdminRevokeSessionsResponse> {
  return superAdminRevokeSessionsResponseSchema.parse(
    await apiPost(`/super-admin/users/${userId}/sessions/${sessionId}/revoke`),
  );
}

export async function revokeAllSuperAdminUserSessions(userId: number): Promise<SuperAdminRevokeSessionsResponse> {
  return superAdminRevokeSessionsResponseSchema.parse(
    await apiPost(`/super-admin/users/${userId}/sessions/revoke-all`),
  );
}

export async function listSuperAdminPlatformAdmins(
  signal?: AbortSignal,
): Promise<SuperAdminPlatformAdminListResponse> {
  return superAdminPlatformAdminListResponseSchema.parse(await apiGet("/super-admin/platform-admins", signal));
}

export async function addSuperAdminPlatformAdmin(email: string): Promise<SuperAdminPlatformAdmin> {
  return superAdminPlatformAdminSchema.parse(await apiPost("/super-admin/platform-admins", { email }));
}

export async function removeSuperAdminPlatformAdmin(platformAdminId: number): Promise<void> {
  await apiDelete(`/super-admin/platform-admins/${platformAdminId}`);
}

export interface SuperAdminAuditLogQuery {
  cursor?: string;
  limit?: number;
  category?: SuperAdminAuditCategory;
  actorKind?: SuperAdminAuditActorKind;
  userId?: number;
  organizationId?: number;
  from?: string;
  to?: string;
}

export async function listSuperAdminAuditLogs(
  query: SuperAdminAuditLogQuery,
  signal?: AbortSignal,
): Promise<SuperAdminAuditLogListResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.category) params.set("category", query.category);
  if (query.actorKind) params.set("actorKind", query.actorKind);
  if (query.userId !== undefined) params.set("userId", String(query.userId));
  if (query.organizationId !== undefined) params.set("organizationId", String(query.organizationId));
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return superAdminAuditLogListResponseSchema.parse(await apiGet(`/super-admin/audit-logs${suffix}`, signal));
}

export async function updateSuperAdminOrganizationEntitlement(
  organizationId: number,
  patch: UpdateOrganizationEntitlementRequest,
  signal?: AbortSignal,
): Promise<OrganizationEntitlementResponse> {
  return organizationEntitlementResponseSchema.parse(
    await apiPatch(`/super-admin/organizations/${organizationId}/entitlement`, patch, signal),
  );
}

export async function getSuperAdminOrganizationMessageBalance(
  organizationId: number,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationMessageBalanceResponse> {
  return superAdminOrganizationMessageBalanceResponseSchema.parse(
    await apiGet(`/super-admin/organizations/${organizationId}/message-balance`, signal),
  );
}

export async function updateSuperAdminOrganizationMessageBalance(
  organizationId: number,
  body: SuperAdminUpdateMessageBalanceRequest,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationMessageBalanceResponse> {
  return superAdminOrganizationMessageBalanceResponseSchema.parse(
    await apiPatch(`/super-admin/organizations/${organizationId}/message-balance`, body, signal),
  );
}
